import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ResultsRowMetadata, ResultsTableMetadata} from 'notebook/integrations/results/ResultsTableConstants';
import ActionFunctionButton from 'notebook/structured-tables/action-functions/ActionFunctionButton';
import {addAPIPendingValues} from 'notebook/structured-tables/mutations/addAPIPendingValues';
import {
  FetchPendingTableRowsQuery,
  FetchPendingTableRowsQueryVariables,
} from 'notebook/structured-tables/queries/__generated/FetchPendingTableRowsQuery';
import {
  FETCH_PENDING_TABLE_ROWS_QUERY,
  PendingTableRow,
} from 'notebook/structured-tables/queries/FETCH_PENDING_TABLE_ROWS_QUERY';
import {
  StructuredTable,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BeaconBadge} from 'ui-library/feedback/BeaconBadge';
import removeNulls from 'util/removeNulls';
import {assertNever} from 'util/Types';

type Props = Pick<
  TableActionFunctionParameters<ResultsTableMetadata, ResultsRowMetadata>,
  'structuredTable' | 'structuredTableAdapter' | 'updateHandler'
>;

export function AddAPIPendingValuesActionButton({
  structuredTable,
  structuredTableAdapter,
  updateHandler,
}: Props): JSX.Element {
  const result = useSkippableBenchlingQuery<FetchPendingTableRowsQuery, FetchPendingTableRowsQueryVariables>(
    FETCH_PENDING_TABLE_ROWS_QUERY,
    {
      variables: {tableId: structuredTable.apiId!},
      renderCachedDataWhenRefetching: true,
      fetchPolicy: 'cache-and-network',
      skip: structuredTable.apiId == null,
    }
  );

  const {tooltip, disabled} = getPendingRowsButtonProps(result, structuredTable);

  const handleClick = (): void => {
    const pendingRows = getNewPendingRows(result, structuredTable);
    if (pendingRows.length === 0) {
      return;
    }
    addAPIPendingValues({
      structuredTable,
      structuredTableAdapter,
      pendingRows,
      updateHandler,
      onComplete: () => {
        getApolloClient().refetchQueries({include: [FETCH_PENDING_TABLE_ROWS_QUERY]});
      },
    });
  };

  return (
    <BeaconBadge anchorOrigin={{vertical: 'top', horizontal: 'right'}} hide={disabled}>
      <ActionFunctionButton
        tooltip={tooltip}
        iconAltText={tooltip}
        disabled={disabled}
        icon="integration"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        tooltipDisabled={false}
        onClick={handleClick}
      />
    </BeaconBadge>
  );
}

/**
 * Filter pending rows to exclude ones that have already been pulled into the table,
 * since the graphql response only updates on page refresh.
 */
function getNewPendingRows(
  result: SkippableBenchlingQueryResult<FetchPendingTableRowsQuery>,
  structuredTable: StructuredTable<ResultsTableMetadata, ResultsRowMetadata>
): Array<PendingTableRow> {
  if (result.type !== QueryResultType.SUCCESS) {
    return [];
  }
  if (isInaccessible(result.data.structuredTable)) {
    return [];
  }
  if (result.data.structuredTable.pendingTableRows.length === 0) {
    return [];
  }
  // We can make this O(n) by using a very simple hashmap instead of searching through an unorganized list
  // of existing euids for every pending row
  const existingEuids = new Set(removeNulls(structuredTable.rows.map((row) => row.metadata.euid)));
  return result.data.structuredTable.pendingTableRows.filter((result) => !existingEuids.has(result.id));
}

function getPendingRowsButtonProps(
  result: SkippableBenchlingQueryResult<FetchPendingTableRowsQuery>,
  structuredTable: StructuredTable<ResultsTableMetadata, ResultsRowMetadata>
): {
  disabled: boolean;
  tooltip: string;
} {
  let count: number;
  switch (result.type) {
    case QueryResultType.SUCCESS:
      count = getNewPendingRows(result, structuredTable).length;
      return {
        tooltip: getTooltip(count),
        disabled: count === 0,
      };
    case QueryResultType.ERROR:
      return {
        tooltip: 'Failed to retrieve pending values from the API',
        disabled: true,
      };
    case QueryResultType.PENDING:
      return {
        tooltip: 'Loading pending values from the API',
        disabled: true,
      };
    case QueryResultType.SKIPPED:
      return {
        tooltip: getTooltip(0),
        disabled: true,
      };
    default:
      return assertNever(result);
  }
}

function getTooltip(count: number): string {
  if (count === 0) {
    return 'No pending values from the API';
  }
  return `Add ${count} pending ${pluralize(count, 'value', 'values')} from the API`;
}
